import { useEffect, useMemo, useRef, useState } from "react";
import { getPublicPartnerInviteCard, submitPartnerApplication } from "./publicPartnerInviteApi.js";
import { getPartnerCountry, partnerCountryName } from "./partnerLetterCountries.js";
import {
  PARTNER_LANGUAGES,
  buildPartnerLetter,
  getPublicPartnerText,
  inferDefaultPartnerLanguage,
} from "./publicPartnerRegisterTranslations.js";
import "./publicInviteRegister.css";
import "./publicPartnerRegister.css";

/**
 * P167. The overseas recruitment agency's own half of partner onboarding: a
 * Super Agent sends this link, the agency reads the invitation letter written
 * for their country, and fills in the Partner Registration Form themselves.
 *
 * Owner: "สร้าง invite card ให้ superagent ใช้เชิญ บริษัท agent ฝั่งแรงงาน เข้าไป
 * agent ครับ ทำรูปแบบที่ agent แรงงานใช้เชิญ แรงงาน เข้าสังกัดเลย" - so this is
 * the same three-step invite flow the worker and employer pages use (closed
 * envelope -> letter -> form), reusing publicInviteRegister.css's own wx-pr-*
 * envelope styling rather than a third copy of it. What differs is the CONTENT
 * and its length: a company's chairman reads a real business letter here, not a
 * three-line note, so the letter view is a proper document.
 *
 * Two honesty rules this page keeps:
 *  - The letter is written for ONE country. If the card names a country this
 *    build has no letter for, the backend already reports it as not found, and
 *    the page never falls back to a letter addressed to somewhere else.
 *  - Submitting registers an application. It is not acceptance into the
 *    network, and the success panel says so and lists what still has to happen.
 *
 * Unauthenticated by design - the agency has no account here and registering
 * does not create one.
 */

// Matches the worker/employer pages exactly - a visual flourish only. The view
// switch is on a short timer and is skipped entirely under prefers-reduced-
// motion, so nothing about correctness depends on the number beyond "short".
const ENVELOPE_OPEN_ANIMATION_MS = 360;

function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function readInviteCodeFromUrl() {
  if (typeof window === "undefined") {
    return "";
  }
  return new URLSearchParams(window.location.search).get("invite")?.trim() ?? "";
}

function getInitials(name) {
  if (!name) {
    return "";
  }
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// The loosest useful check, deliberately identical in spirit to the backend's
// own: catch an address that could never be written to, never reject a valid
// foreign one.
function looksLikeEmail(value) {
  return /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(value);
}

const EMPTY_FORM = {
  companyName: "",
  recruitmentLicenseNumber: "",
  yearEstablished: "",
  officeLocation: "",
  websiteUrl: "",
  workerSupplyCapacity: "",
  mainWorkerCategories: "",
  countriesCurrentlyServed: "",
  contactPersonName: "",
  contactPersonPosition: "",
  mobileNumber: "",
  whatsAppNumber: "",
  emailAddress: "",
};

export default function PublicPartnerRegisterPage() {
  const [inviteCode] = useState(readInviteCodeFromUrl);
  const [lookup, setLookup] = useState({ status: inviteCode ? "loading" : "missing-invite", data: null, error: null });
  const [view, setView] = useState("envelope"); // "envelope" | "letter" | "form"
  const [envelopeOpening, setEnvelopeOpening] = useState(false);
  const envelopeTimerRef = useRef(null);

  // Defaults to English - this letter's reader is abroad - and switches to the
  // invite's own intended language the moment the card loads, unless the reader
  // has already picked a chip themselves (then their choice wins).
  const [language, setLanguageState] = useState("en");
  const languageTouchedRef = useRef(false);

  const [form, setForm] = useState(EMPTY_FORM);
  const [validationError, setValidationError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successResponse, setSuccessResponse] = useState(null);

  const t = getPublicPartnerText(language);

  function handleLanguageChange(code) {
    languageTouchedRef.current = true;
    setLanguageState(code);
  }

  useEffect(() => {
    if (!inviteCode) {
      return undefined;
    }

    let cancelled = false;
    getPublicPartnerInviteCard(inviteCode)
      .then((data) => {
        if (!cancelled) {
          setLookup({ status: "ready", data, error: null });
          if (!languageTouchedRef.current) {
            const inferred = inferDefaultPartnerLanguage(data);
            if (inferred) {
              setLanguageState(inferred);
            }
          }
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setLookup({ status: "error", data: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inviteCode]);

  useEffect(
    () => () => {
      if (envelopeTimerRef.current) {
        window.clearTimeout(envelopeTimerRef.current);
      }
    },
    []
  );

  const card = lookup.status === "ready" ? lookup.data : null;

  const letter = useMemo(() => {
    if (!card) {
      return null;
    }
    return buildPartnerLetter(language, card.partnerCountryCode, {
      appName: card.appName || "WorkMateX",
      senderName: card.superAgentName,
    });
  }, [card, language]);

  function handleOpenEnvelope() {
    if (prefersReducedMotion()) {
      setView("letter");
      return;
    }
    setEnvelopeOpening(true);
    envelopeTimerRef.current = window.setTimeout(() => setView("letter"), ENVELOPE_OPEN_ANIMATION_MS);
  }

  function updateField(field, value) {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    // Mirrors the backend's required set exactly - never stricter, so the form
    // cannot refuse something the API would have accepted.
    if (!form.companyName.trim()) {
      setValidationError(t.errorCompanyNameRequired);
      return;
    }
    if (!form.recruitmentLicenseNumber.trim()) {
      setValidationError(t.errorLicenseRequired);
      return;
    }
    if (!form.contactPersonName.trim()) {
      setValidationError(t.errorContactNameRequired);
      return;
    }
    if (!form.mobileNumber.trim()) {
      setValidationError(t.errorMobileRequired);
      return;
    }
    if (!form.emailAddress.trim()) {
      setValidationError(t.errorEmailRequired);
      return;
    }
    if (!looksLikeEmail(form.emailAddress.trim())) {
      setValidationError(t.errorEmailInvalid);
      return;
    }

    const trimmedYear = form.yearEstablished.trim();
    let yearEstablished;
    if (trimmedYear) {
      const parsed = Number(trimmedYear);
      if (!Number.isInteger(parsed) || parsed < 1800 || parsed > new Date().getFullYear()) {
        setValidationError(t.errorYearInvalid);
        return;
      }
      yearEstablished = parsed;
    }

    setValidationError(null);
    setSubmitting(true);
    try {
      const response = await submitPartnerApplication({
        inviteCode,
        companyName: form.companyName.trim(),
        recruitmentLicenseNumber: form.recruitmentLicenseNumber.trim(),
        yearEstablished,
        officeLocation: form.officeLocation.trim() || undefined,
        websiteUrl: form.websiteUrl.trim() || undefined,
        workerSupplyCapacity: form.workerSupplyCapacity.trim() || undefined,
        mainWorkerCategories: form.mainWorkerCategories.trim() || undefined,
        countriesCurrentlyServed: form.countriesCurrentlyServed.trim() || undefined,
        contactPersonName: form.contactPersonName.trim(),
        contactPersonPosition: form.contactPersonPosition.trim() || undefined,
        mobileNumber: form.mobileNumber.trim(),
        whatsAppNumber: form.whatsAppNumber.trim() || undefined,
        emailAddress: form.emailAddress.trim(),
      });

      // A link that ran out between opening the letter and submitting comes
      // back as available:false, not as an error.
      if (response?.available === false) {
        setLookup((previous) => ({
          ...previous,
          data: { ...(previous.data ?? {}), available: false, unavailableReason: response.unavailableReason },
        }));
        return;
      }

      setSuccessResponse(response);
    } catch (error) {
      setValidationError(error?.message ?? t.submitGenericError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="wx-pr-root wx-ptr-root">
      <Topbar t={t} appName={card?.appName} language={language} onLanguageChange={handleLanguageChange} />

      {lookup.status === "missing-invite" && <CenteredState title={t.missingInviteTitle} body={t.missingInviteBody} />}

      {lookup.status === "loading" && (
        <div className="wx-pr-center-wrap">
          <div className="wx-pr-spinner" aria-hidden="true" />
          <p className="wx-pr-loading-text">{t.loadingText}</p>
        </div>
      )}

      {lookup.status === "error" && (
        <CenteredState
          title={lookup.error?.status === 404 ? t.errorTitleNotFound : t.errorTitleGeneric}
          body={lookup.error?.message ?? t.errorBodyGeneric}
        />
      )}

      {card && !card.available && (
        <CenteredState
          title={t.unavailableTitle}
          /* The backend's reason is written for this reader - show it rather
             than replacing it with a generic sentence. */
          body={card.unavailableReason ?? t.unavailableDefault}
        />
      )}

      {card && card.available && successResponse && (
        <div className="wx-pr-center-wrap">
          <SuccessPanel t={t} response={successResponse} />
        </div>
      )}

      {card && card.available && !successResponse && view === "envelope" && (
        <main className="wx-pr-envelope-screen">
          <EnvelopePanel t={t} card={card} language={language} opening={envelopeOpening} onOpen={handleOpenEnvelope} />
        </main>
      )}

      {card && card.available && !successResponse && view === "letter" && letter && (
        <main className="wx-ptr-letter-screen">
          <LetterPanel t={t} card={card} letter={letter} onStart={() => setView("form")} />
        </main>
      )}

      {card && card.available && !successResponse && view === "form" && (
        <main className="wx-ptr-form-screen">
          <FormPanel
            t={t}
            card={card}
            language={language}
            form={form}
            onChange={updateField}
            onBack={() => setView("letter")}
            onSubmit={handleSubmit}
            submitting={submitting}
            validationError={validationError}
          />
        </main>
      )}
    </div>
  );
}

function Topbar({ t, appName, language, onLanguageChange }) {
  return (
    <header className="wx-ptr-topbar">
      <span className="wx-ptr-brand">{appName ?? "WorkMateX"}</span>
      <div className="wx-ptr-topbar-right">
        <span className="wx-ptr-topbar-tag">{t.topbarTag}</span>
        <div className="wx-ptr-lang-group">
          {PARTNER_LANGUAGES.map((chip) => (
            <button
              key={chip.code}
              type="button"
              className={`wx-pr-lang-chip${language === chip.code ? " wx-pr-lang-chip--active" : ""}`}
              aria-pressed={language === chip.code}
              onClick={() => onLanguageChange(chip.code)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}

function CenteredState({ title, body }) {
  return (
    <div className="wx-pr-center-wrap">
      <section className="wx-ptr-state-card">
        <h1>{title}</h1>
        <p>{body}</p>
      </section>
    </div>
  );
}

/** The closed envelope. Identical motion to the worker/employer pages. */
function EnvelopePanel({ t, card, language, opening, onOpen }) {
  const senderName = card.superAgentName || t.envelopeFallbackSender;
  const initials = getInitials(senderName);
  const country = partnerCountryName(card.partnerCountryCode, language);

  return (
    <section className={`wx-pr-envelope-card${opening ? " wx-pr-envelope-card--opening" : ""}`}>
      <div className="wx-pr-envelope-visual" aria-hidden="true">
        <div className="wx-pr-envelope-flap" />
        <div className="wx-pr-envelope-body">
          <div className="wx-pr-envelope-seal">{initials || "WX"}</div>
        </div>
      </div>
      <span className="wx-pr-envelope-eyebrow">{t.envelopeEyebrow}</span>
      <p className="wx-pr-envelope-from">{t.envelopeFrom(senderName)}</p>
      {country && <p className="wx-ptr-envelope-country">{country}</p>}
      <p className="wx-pr-envelope-hint">{t.envelopeHint}</p>
      <button type="button" className="wx-pr-submit-button wx-pr-envelope-cta" onClick={onOpen}>
        {t.envelopeOpenCta}
      </button>
    </section>
  );
}

/** A process flow ("A → B → C") as real, wrapping steps rather than one long line. */
function FlowSteps({ steps }) {
  return (
    <ol className="wx-ptr-flow">
      {steps.map((step, index) => (
        <li key={step} className="wx-ptr-flow-step">
          <span className="wx-ptr-flow-index" aria-hidden="true">
            {index + 1}
          </span>
          <span>{step}</span>
        </li>
      ))}
    </ol>
  );
}

function ChipList({ items }) {
  return (
    <ul className="wx-ptr-chips">
      {items.map((item) => (
        <li key={item} className="wx-ptr-chip">
          {item}
        </li>
      ))}
    </ul>
  );
}

/** The letter itself - shown only after the envelope has been opened. */
function LetterPanel({ t, card, letter, onStart }) {
  return (
    <article className="wx-ptr-letter">
      <header className="wx-ptr-letter-head">
        <p className="wx-ptr-letter-subject">{letter.subject}</p>
        <p className="wx-ptr-letter-salutation">{letter.salutation}</p>
      </header>

      {letter.opening.map((paragraph) => (
        <p key={paragraph} className="wx-ptr-letter-para">
          {paragraph}
        </p>
      ))}

      {card.noteToPartner && (
        <aside className="wx-ptr-note">
          <span className="wx-ptr-note-label">{t.letterNoteLabel}</span>
          <p>{card.noteToPartner}</p>
        </aside>
      )}

      {letter.sections.map((section) => (
        <section key={section.id} className="wx-ptr-letter-section">
          <h2 className="wx-ptr-letter-heading">{section.heading}</h2>
          {(section.paragraphs ?? []).map((paragraph) => (
            <p key={paragraph} className="wx-ptr-letter-para">
              {paragraph}
            </p>
          ))}
          {section.flow && <FlowSteps steps={section.flow} />}
          {(section.afterFlow ?? []).map((paragraph) => (
            <p key={paragraph} className="wx-ptr-letter-para">
              {paragraph}
            </p>
          ))}
          {section.bullets && (
            <ul className="wx-ptr-letter-bullets">
              {section.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          )}
          {section.afterChipsHeading && <p className="wx-ptr-letter-para">{section.afterChipsHeading}</p>}
          {section.chips && <ChipList items={section.chips} />}
          {(section.afterChips ?? []).map((paragraph) => (
            <p key={paragraph} className="wx-ptr-letter-para">
              {paragraph}
            </p>
          ))}
        </section>
      ))}

      <p className="wx-ptr-letter-tagline">
        {letter.tagline.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </p>

      <footer className="wx-ptr-signature">
        <p>{letter.closing}</p>
        <p className="wx-ptr-signature-name">{letter.signatureName}</p>
        <p className="wx-ptr-signature-unit">{letter.signatureUnit}</p>
        <p className="wx-ptr-signature-tagline">{letter.signatureTagline}</p>
      </footer>

      <div className="wx-ptr-letter-cta">
        <button type="button" className="wx-pr-submit-button" onClick={onStart}>
          {t.letterStartCta}
        </button>
      </div>
    </article>
  );
}

function Field({ id, label, required, hint, children }) {
  return (
    <div className="wx-ptr-field">
      <label htmlFor={id}>
        {label}
        {required && <b aria-hidden="true"> *</b>}
      </label>
      {children}
      {hint && <span className="wx-ptr-field-hint">{hint}</span>}
    </div>
  );
}

function FormPanel({ t, card, language, form, onChange, onBack, onSubmit, submitting, validationError }) {
  const senderName = card.superAgentName || t.envelopeFallbackSender;
  const country = getPartnerCountry(card.partnerCountryCode);
  // The one country-specific field label in the whole form - "Bangladesh
  // Recruitment License / RL Number" rather than a generic "licence number".
  const licenseLabel = country?.licenseLabel?.[language] ?? country?.licenseLabel?.en ?? "";

  return (
    <section className="wx-ptr-form-card">
      <div className="wx-ptr-form-head">
        <button type="button" className="wx-ptr-back" onClick={onBack}>
          {t.formBack}
        </button>
        <h1>{t.formHeading}</h1>
        <p className="wx-ptr-muted">{t.formInviteFrom(senderName)}</p>
        <p className="wx-ptr-muted">{t.formRequiredHint}</p>
      </div>

      <form className="wx-ptr-form" onSubmit={onSubmit}>
        <p className="wx-ptr-section-heading">{t.formCompanySection}</p>

        <Field id="partner-company-name" label={t.fieldCompanyName} required>
          <input
            id="partner-company-name"
            type="text"
            value={form.companyName}
            onChange={(event) => onChange("companyName", event.target.value)}
            placeholder={t.fieldCompanyNamePlaceholder}
          />
        </Field>

        <Field id="partner-license" label={licenseLabel} required>
          <input
            id="partner-license"
            type="text"
            value={form.recruitmentLicenseNumber}
            onChange={(event) => onChange("recruitmentLicenseNumber", event.target.value)}
            placeholder={country?.licensePlaceholder ?? ""}
          />
        </Field>

        <div className="wx-ptr-field-row">
          <Field id="partner-year" label={t.fieldYearEstablished}>
            <input
              id="partner-year"
              type="number"
              min="1800"
              step="1"
              inputMode="numeric"
              value={form.yearEstablished}
              onChange={(event) => onChange("yearEstablished", event.target.value)}
              placeholder={t.fieldYearEstablishedPlaceholder}
            />
          </Field>

          <Field id="partner-office" label={t.fieldOfficeLocation}>
            <input
              id="partner-office"
              type="text"
              value={form.officeLocation}
              onChange={(event) => onChange("officeLocation", event.target.value)}
              placeholder={t.fieldOfficeLocationPlaceholder}
            />
          </Field>
        </div>

        <Field id="partner-website" label={t.fieldWebsite}>
          <input
            id="partner-website"
            type="text"
            value={form.websiteUrl}
            onChange={(event) => onChange("websiteUrl", event.target.value)}
            placeholder={t.fieldWebsitePlaceholder}
          />
        </Field>

        <Field id="partner-capacity" label={t.fieldCapacity}>
          <input
            id="partner-capacity"
            type="text"
            value={form.workerSupplyCapacity}
            onChange={(event) => onChange("workerSupplyCapacity", event.target.value)}
            placeholder={t.fieldCapacityPlaceholder}
          />
        </Field>

        <Field id="partner-categories" label={t.fieldCategories}>
          <textarea
            id="partner-categories"
            rows={2}
            value={form.mainWorkerCategories}
            onChange={(event) => onChange("mainWorkerCategories", event.target.value)}
            placeholder={t.fieldCategoriesPlaceholder}
          />
        </Field>

        <Field id="partner-countries" label={t.fieldCountriesServed}>
          <input
            id="partner-countries"
            type="text"
            value={form.countriesCurrentlyServed}
            onChange={(event) => onChange("countriesCurrentlyServed", event.target.value)}
            placeholder={t.fieldCountriesServedPlaceholder}
          />
        </Field>

        <div className="wx-ptr-field-divider" aria-hidden="true" />
        <p className="wx-ptr-section-heading">{t.formContactSection}</p>

        <div className="wx-ptr-field-row">
          <Field id="partner-contact-name" label={t.fieldContactName} required>
            <input
              id="partner-contact-name"
              type="text"
              value={form.contactPersonName}
              onChange={(event) => onChange("contactPersonName", event.target.value)}
            />
          </Field>

          <Field id="partner-position" label={t.fieldPosition}>
            <input
              id="partner-position"
              type="text"
              value={form.contactPersonPosition}
              onChange={(event) => onChange("contactPersonPosition", event.target.value)}
              placeholder={t.fieldPositionPlaceholder}
            />
          </Field>
        </div>

        <div className="wx-ptr-field-row">
          <Field id="partner-mobile" label={t.fieldMobile} required>
            <input
              id="partner-mobile"
              type="tel"
              value={form.mobileNumber}
              onChange={(event) => onChange("mobileNumber", event.target.value)}
              placeholder={t.fieldMobilePlaceholder}
            />
          </Field>

          <Field id="partner-whatsapp" label={t.fieldWhatsApp}>
            <input
              id="partner-whatsapp"
              type="tel"
              value={form.whatsAppNumber}
              onChange={(event) => onChange("whatsAppNumber", event.target.value)}
              placeholder={t.fieldMobilePlaceholder}
            />
          </Field>
        </div>

        <Field id="partner-email" label={t.fieldEmail} required>
          <input
            id="partner-email"
            type="email"
            value={form.emailAddress}
            onChange={(event) => onChange("emailAddress", event.target.value)}
            placeholder={t.fieldEmailPlaceholder}
          />
        </Field>

        {validationError && (
          <p className="wx-ptr-error" role="alert">
            {validationError}
          </p>
        )}

        <button type="submit" className="wx-pr-submit-button" disabled={submitting}>
          {submitting ? t.submitButtonSubmitting : t.submitButton}
        </button>

        <p className="wx-ptr-muted wx-ptr-fineprint">{t.formFineprint}</p>
      </form>
    </section>
  );
}

function SuccessPanel({ t, response }) {
  const name = response?.superAgentName ?? t.successFallbackName;
  return (
    <section className="wx-ptr-state-card">
      <h1>{t.successTitle}</h1>
      <p>{response?.isDuplicate ? t.successDuplicateBody : t.successBody}</p>
      <p className="wx-ptr-muted">{t.successFollowup(name)}</p>

      <p className="wx-ptr-section-heading">{t.successNextHeading}</p>
      <ol className="wx-ptr-next-steps">
        {t.successNextSteps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </section>
  );
}
